import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { fetchJson } from "@/lib/http";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose } from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, CalendarDays, DollarSign, Clock, Star, Users, TrendingUp, Settings, Plus, Brain, MessageSquare, ExternalLink, X, Lock, Crown, FileText, Wrench, Repeat, Layers } from "lucide-react";
import CompanyInvoicesPanel from "@/components/company/CompanyInvoicesPanel";
import { useToast } from "@/hooks/use-toast";
import { useMfaStepUp } from "@/hooks/useMfaStepUp";
import CalendarView from "@/components/company/CalendarView";
import PerformanceAnalytics from "@/components/company/PerformanceAnalytics";
import CompanyAdvancedAnalytics from "@/components/company/CompanyAdvancedAnalytics";
import ServiceBuilder from "@/components/company/ServiceBuilder";
import SmartPriceEstimator from "@/components/company/SmartPriceEstimator";
import QuoteRequestManager from "@/components/company/QuoteRequestManager";
import BookingInbox from "@/components/company/BookingInbox";
import BulkOrdersPanel from "@/components/company/BulkOrdersPanel";
import UtmAnalytics from "@/components/company/UtmAnalytics";
import AvailabilityEditor from "@/components/company/AvailabilityEditor";
import RecurringBookingManager from "@/components/company/RecurringBookingManager";
import SeasonalOfferBuilder from "@/components/company/SeasonalOfferBuilder";
import ExternalBookingsTracker from "@/components/company/externalBookings/ExternalBookingsTracker";
import { SUBSCRIPTIONS_ENABLED } from "@/config/features";

/** Maps DB `services` row → ServiceBuilder `existingService` (incl. pricing_rules + booking_policy). */
function mapDbServiceForServiceBuilder(s: Record<string, any>) {
  const pt = String(s.pricing_type || "fixed");
  const pricingType = ["fixed", "hourly", "per_sqm", "custom"].includes(pt) ? pt : "fixed";
  return {
    name: String(s.name || ""),
    description: String(s.description || ""),
    category_id: String(s.category_id || ""),
    base_price: Number(s.base_price) || 0,
    pricing_type: pricingType as "fixed" | "hourly" | "per_sqm" | "custom",
    duration_minutes: Number(s.duration_minutes) || 60,
    requires_quote: !!s.requires_quote,
    auto_quote_rules:
      typeof s.auto_quote_rules === "object" && s.auto_quote_rules !== null ? s.auto_quote_rules : {},
    included_items: Array.isArray(s.included_items) ? s.included_items : [],
    benefit_items: Array.isArray(s.benefit_items) ? s.benefit_items : [],
    vendor_covers_customer_fee: !!s.vendor_covers_customer_fee,
    display_fee_disclaimer: !!s.display_fee_disclaimer,
    allowed_service_types: Array.isArray(s.allowed_service_types) ? s.allowed_service_types : [],
    accepted_payment_methods:
      Array.isArray(s.accepted_payment_methods) && s.accepted_payment_methods.length
        ? s.accepted_payment_methods
        : ["card", "cash"],
    pricing_rules: s.pricing_rules || {},
    booking_policy: s.booking_policy || {},
  };
}

function OffersList({ companyId, services, refreshKey = 0, requireAAL2 }: { companyId: string; services: any[]; refreshKey?: number; requireAAL2?: () => Promise<boolean> }) {
  const [rows, setRows] = useState<any[]>([]);
  const { toast } = useToast();
  const { t, tx } = useI18n();

  useEffect(() => {
    (async () => {
      try {
        if (!companyId) return;
        const { data, error } = await (supabase as any)
          .from('seasonal_offers')
          .select('id, service_id, name, discount_percent, start_date, end_date, is_active')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setRows(data || []);
      } catch (e: any) {
        toast({ title: t("vendor.offers.load_failed"), description: e.message || t("vendor.offers.load_failed_desc"), variant: 'destructive' });
      }
    })();
  }, [companyId, refreshKey]);

  const svcName = (id: string) => services.find((s:any) => s.id === id)?.name || t("profile.fallback.service");

  return (
    <div className="space-y-3">
      {rows.length === 0 && <div className="text-sm text-gray-600">{t("vendor.offers.none")}</div>}
      {rows.map((r) => (
        <div key={r.id} className="p-3 border rounded-lg flex items-center justify-between">
          <div className="space-y-1">
            <div className="font-medium">{r.name} <span className="text-sm text-gray-500">(-{r.discount_percent}%)</span></div>
            <div className="text-xs text-gray-600">{svcName(r.service_id)} • {r.start_date} → {r.end_date}</div>
            <div className="text-xs">{r.is_active ? t("vendor.offers.status_active") : t("vendor.offers.status_inactive")}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="destructive" size="sm" onClick={async () => {
              if (!window.confirm(t("vendor.offers.delete_confirm"))) return;
              try {
                if (requireAAL2 && !(await requireAAL2())) return;
                const { error } = await (supabase as any)
                  .from('seasonal_offers')
                  .delete()
                  .eq('id', r.id);
                if (error) throw error;
                setRows(prev => prev.filter(x => x.id !== r.id));
              } catch (e: any) {
                toast({ title: t("vendor.offers.delete_failed"), description: e.message || t("vendor.toast.error"), variant: 'destructive' });
              }
            }}>{t("vendor.common.delete")}</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

import { Booking, Company, DashboardStats } from "@/types";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import { Seo } from "@/components/seo/Seo";

// Feature flag: the Google Calendar OAuth flow is built but the production
// Google project / consent screen is not yet approved, so vendors get an
// error if they click "Connetti Google Calendar". Hide the connect banner
// until the Google side is ready — flip to true to re-enable.
const GOOGLE_CALENDAR_ENABLED = false;

// Feature flag: the quote-request flow (customer asks for a custom price,
// vendor responds) is still being iterated on. Hide the vendor tab and the
// customer-facing CTAs until the flow is finalized — flip to true to bring
// the existing UI back online without touching the components.
const QUOTES_ENABLED = false;

export default function CompanyDashboard() {
  const { t, lang, tx } = useI18n();
  const [company, setCompany] = useState<Company | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [editingService, setEditingService] = useState<any | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalEarnings: 0,
    pendingPayments: 0,
    pendingFromCash: 0,
    pendingFromInvoice: 0,
    totalBookings: 0,
    completedBookings: 0,
    averageRating: 0,
    totalReviews: 0,
  });
  const [loading, setLoading] = useState(true);
  const [calendarAccount, setCalendarAccount] = useState<any | null>(null);
  const [showGoogleConnectBanner, setShowGoogleConnectBanner] = useState(false);
  const [stripeReady, setStripeReady] = useState<boolean | null>(null);
  const [stripeConnectHint, setStripeConnectHint] = useState<string | null>(null);
  const [stripeRequirements, setStripeRequirements] = useState<string[]>([]);
  const [internalHold, setInternalHold] = useState<boolean>(false);
  const [disputeRate, setDisputeRate] = useState<number>(0);
  const [isProUser, setIsProUser] = useState(false);
  const [b2bSubscription, setB2bSubscription] = useState<{ company_id: string; plan: string; status: string; stripe_customer_id: string | null; expires_at: string | null; cancelled_at: string | null; updated_at: string } | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [opsOpen, setOpsOpen] = useState(false);
  const [opsBooking, setOpsBooking] = useState<any | null>(null);
  const [mEst, setMEst] = useState<string>('');
  const [mAct, setMAct] = useState<string>('');
  const [mTravel, setMTravel] = useState<string>('');
  const [mMaterials, setMMaterials] = useState<string>('');
  const [checkItem, setCheckItem] = useState<string>('');
  const [checkRequired, setCheckRequired] = useState<boolean>(false);
  const [incidentCode, setIncidentCode] = useState<string>('');
  const [incidentNote, setIncidentNote] = useState<string>('');
  const [incidentTypes, setIncidentTypes] = useState<any[]>([]);
  const [offersRefreshKey, setOffersRefreshKey] = useState<number>(0);
  const [acceptedVendorPayoutTerms, setAcceptedVendorPayoutTerms] = useState(false);
  const [resDrawerOpen, setResDrawerOpen] = useState(false);
  const [resDraft, setResDraft] = useState<{ bookingId: string; newDate: Date | null; newTime: string; reason: string }>({ bookingId: "", newDate: null, newTime: "", reason: "" });
  const [cancelDrawerOpen, setCancelDrawerOpen] = useState(false);
  const [cancelDraft, setCancelDraft] = useState<{ bookingId: string; reason: string }>({ bookingId: "", reason: "" });
  const [cancelEstimate, setCancelEstimate] = useState<{
    total_amount?: number;
    customer_fee_cents?: number | null;
    hours_until_service?: number;
    within48?: boolean;
    calculated_retained_cents?: number;
    calculated_refund_cents?: number;
  } | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { ensureAAL2, StepUpDialog } = useMfaStepUp();

  const requireAAL2 = async () => {
    const ok = await ensureAAL2();
    if (!ok) {
      toast({
title: t("vendor.toast.verification_required"),
        description: t("vendor.toast.verification_2fa"),
        variant: "destructive",
      });
    }
    return ok;
  };

  useEffect(() => {
    checkAuthAndFetchData();
  }, []);

  // Ensure dashboard starts at top on entry
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    } catch {}
  }, []);

  // Deep-link handler: when the dashboard is opened with ?tab=services&edit=<id>
  // (typically from CompanyProfile), pre-select the matching service in the
  // builder. When opened with ?new=1, ensure we render the empty builder. Runs
  // every time `services` changes so the link works regardless of which loads
  // first (the URL or the services list).
  useEffect(() => {
    const editId = searchParams.get("edit");
    const wantsNew = searchParams.get("new") === "1";

    if (wantsNew) {
      if (editingService !== null) setEditingService(null);
      return;
    }

    if (editId && services.length > 0) {
      // Only update if not already pointing at the right service to avoid
      // re-mounting the builder on every render.
      if (!editingService || editingService.id !== editId) {
        const match = services.find((s: any) => s.id === editId);
        if (match) setEditingService(match);
      }
    }
  }, [searchParams, services]);

  async function startStripeOnboarding() {
    try {
      if (!company) return;
      if (!(await requireAAL2())) return;
      await supabase.auth.refreshSession();
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
toast({ title: t("vendor.toast.session_unavailable"), description: t("vendor.toast.session_relogin"), variant: "destructive" });
        return;
      }
      let r = await fetch('/api/ops?action=stripe_onboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company_id: (company as any).id, access_token: token })
      });
      let js = await r.json().catch(() => ({} as any));
      if (r.status === 401) {
        await supabase.auth.refreshSession();
        const next = await supabase.auth.getSession();
        const retryToken = next?.data?.session?.access_token;
        if (retryToken) {
          r = await fetch('/api/ops?action=stripe_onboard', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${retryToken}` },
            body: JSON.stringify({ company_id: (company as any).id, access_token: retryToken })
          });
          js = await r.json().catch(() => ({} as any));
        }
      }
      if (!r.ok) {
        const msg401 = js?.hint || t('vendor.toast.session_expired');
        const hint = typeof (js as any)?.hint === 'string' ? (js as any).hint : '';
        const host = typeof (js as any)?.supabase_host === 'string' ? (js as any).supabase_host : '';
        const detail =
          r.status === 401
            ? msg401
            : [js?.error || js?.message || t('vendor.toast.stripe_onboard_failed'), (js as any)?.step, hint, host ? `DB: ${host}` : '']
                .filter(Boolean)
                .join(' — ');
        throw new Error(detail);
      }
      if (js?.url) {
        window.location.assign(js.url);
        return;
      }
      throw new Error(t('vendor.toast.stripe_onboard_link_unavailable'));
    } catch (e: any) {
      toast({ title: t('vendor.toast.stripe'), description: e?.message || t('vendor.toast.error'), variant: 'destructive' });
    }
  }

  async function openStripeDashboard() {
    try {
      if (!company) return;
      if (!(await requireAAL2())) return;
      await supabase.auth.refreshSession();
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
toast({ title: t("vendor.toast.session_unavailable"), description: t("vendor.toast.session_relogin"), variant: "destructive" });
        return;
      }
      const cid = encodeURIComponent((company as any).id);
      let r = await fetch(`/api/ops?action=stripe_dashboard_link&company_id=${cid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company_id: (company as any).id, access_token: token })
      });
      let js = await r.json().catch(() => ({} as any));
      if (r.status === 401) {
        await supabase.auth.refreshSession();
        const next = await supabase.auth.getSession();
        const retryToken = next?.data?.session?.access_token;
        if (retryToken) {
          r = await fetch(`/api/ops?action=stripe_dashboard_link&company_id=${cid}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${retryToken}` },
            body: JSON.stringify({ company_id: (company as any).id, access_token: retryToken })
          });
          js = await r.json().catch(() => ({} as any));
        }
      }
      if (!r.ok) {
        const hint = typeof (js as any)?.hint === 'string' ? (js as any).hint : '';
        const mode = typeof (js as any)?.mode === 'string' ? (js as any).mode : '';
        throw new Error(
          r.status === 401
? t('vendor.toast.session_expired')
            : [js?.error || js?.message || t('vendor.toast.stripe_dashboard_failed'), hint, mode ? tx('vendor.toast.api_mode', { mode }) : ''].filter(Boolean).join(' — ')
        );
      }
      if (js?.url) {
        window.location.assign(js.url);
        return;
      }
      throw new Error(t('vendor.toast.stripe_dashboard_link_unavailable'));
    } catch (e: any) {
      toast({ title: t('vendor.toast.stripe'), description: e?.message || t('vendor.toast.error'), variant: 'destructive' });
    }
  }


  const checkAuthAndFetchData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      // Resolve profile id first
      const { data: profRow, error: profErr } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (profErr) throw profErr;
      const profileId = profRow?.id as string | undefined;
      if (!profileId) { navigate('/company/onboarding'); return; }

      // Choose the most appropriate company when multiple exist:
      // 1) latest onboarding_completed=true and not deleted/inactive
      // 2) else latest active/not-deleted
      let companyData: any | null = null;
      const { data: completedList, error: compErr1 } = await (supabase as any)
        .from('companies')
        .select('*')
        .eq('profile_id', profileId)
        .eq('is_deleted', false)
        .eq('is_active', true)
        .eq('onboarding_completed', true)
        .order('created_at', { ascending: false })
        .limit(1);
      if (compErr1) throw compErr1;
      if (completedList && completedList.length > 0) {
        companyData = completedList[0];
      } else {
        const { data: activeList, error: compErr2 } = await (supabase as any)
          .from('companies')
          .select('*')
          .eq('profile_id', profileId)
          .eq('is_deleted', false)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1);
        if (compErr2) throw compErr2;
        companyData = (activeList && activeList.length > 0) ? activeList[0] : null;
      }

      if (!companyData) { navigate('/company/onboarding'); return; }

      // Final guard
      if (companyData.is_deleted === true || companyData.is_active === false) {
        navigate('/company/onboarding');
        return;
      }

      if (!companyData.onboarding_completed) {
        navigate('/company/onboarding');
        return;
      }

      setCompany(companyData as any);
      setAcceptedVendorPayoutTerms(!!(companyData as any).vendor_payments_terms_accepted);

      // Stripe status for onboarding gating: align the dashboard with the stricter READY
      // criteria used server-side before routing live card volume.
      try {
        await supabase.auth.refreshSession();
        const { data: { session: sess } } = await supabase.auth.getSession();
        const token = sess?.access_token;
        const r = await fetch(`/api/ops?action=stripe_account_status&company_id=${encodeURIComponent(companyData.id)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const js = await r.json().catch(() => ({}));
        if (!r.ok) {
          setStripeReady(null);
          setStripeConnectHint(null);
        } else {
          const ready = js?.derived?.vendor_ready === true;
          setStripeReady(ready);
          setStripeConnectHint(
            ready
              ? null
              : typeof (js as any)?.connect_hint === 'string' && (js as any).connect_hint.trim()
                ? String((js as any).connect_hint).trim()
                : null
          );
          const due = [
            ...(Array.isArray(js?.requirements?.currently_due) ? js.requirements.currently_due : []),
            ...(Array.isArray(js?.requirements?.past_due) ? js.requirements.past_due : []),
            ...(Array.isArray(js?.requirements?.pending_verification) ? js.requirements.pending_verification : []),
          ].map(String);
          setStripeRequirements(due);
          setInternalHold(!!js?.internal_hold);
          setDisputeRate(Number(js?.dispute_rate_recent || 0));
        }
      } catch {}

      // Fetch calendar account (Google) for banner state
      const { data: calAcc } = await (supabase as any)
        .from("calendar_accounts")
        .select("id, account_email, provider, access_token, token_expires_at")
        .eq("profile_id", (companyData as any).profile_id)
        .eq("provider", "google")
        .maybeSingle();
      
      // Check if token is valid (not expired)
      let isTokenValid = false;
      if (calAcc && calAcc.access_token && calAcc.token_expires_at) {
        const now = new Date();
        const expiresAt = new Date(calAcc.token_expires_at);
        isTokenValid = expiresAt > now;
      }
      
      setCalendarAccount(calAcc || null);
      setShowGoogleConnectBanner(!calAcc || !isTokenValid); // Show banner if no account or token expired

      // Fetch services
      const { data: servicesData, error: servicesError } = await supabase
        .from("services")
        .select("*")
        .eq("company_id", companyData.id);

      if (servicesError) throw servicesError;
      setServices(servicesData || []);

      // Fetch categories for Service Builder
      const { data: cats } = await supabase
        .from("service_categories")
        .select("id, name")
        .order("name");
      setCategories(cats || []);

      // Fetch incident types for reporting
      const { data: incidentTypesData } = await (supabase as any)
        .from("incident_types")
        .select("id, code, label, category")
        .order("label");
      setIncidentTypes(incidentTypesData || []);

      // Check PRO subscription status (prefer RPC to avoid RLS mismatch)
      try {
        const { data: isPro, error: isProError } = await (supabase as any)
          .rpc('is_pro_company', { p_company_id: companyData.id });
        if (isProError) throw isProError;
        setIsProUser(!!isPro);
      } catch (_) {
        try {
          const nowIso = new Date().toISOString();
        const { data: subscriptionData } = await (supabase as any)
          .from('subscriptions')
            .select('plan, status, expires_at, created_at')
          .eq('company_id', companyData.id)
          .eq('status', 'active')
            .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
        setIsProUser(subscriptionData?.plan === 'pro' || false);
      } catch (_) {
        setIsProUser(false);
        }
      }

      // Active B2B subscription (for "Il mio piano" and Gestisci abbonamento)
      try {
        const { data: subRows, error: subErr } = await (supabase as any).rpc('get_active_b2b_subscription', { p_company_id: companyData.id });
        if (!subErr && Array.isArray(subRows) && subRows.length > 0) {
          const s = subRows[0];
          setB2bSubscription({
            company_id: s.company_id || '',
            plan: s.plan || '',
            status: s.status || 'active',
            stripe_customer_id: s.stripe_customer_id || null,
            expires_at: s.expires_at || null,
            cancelled_at: s.cancelled_at || null,
            updated_at: s.updated_at || '',
          });
        } else {
          setB2bSubscription(null);
        }
      } catch (_) {
        setB2bSubscription(null);
      }

      // Fetch bookings with service details
      const { data: bookingsData, error: bookingsError } = await supabase
        .from("bookings")
        .select(`
          *,
          services (
            name,
            duration_minutes
          ),
          service_addresses (
            address_line1,
            address_line2,
            city,
            postal_code
          )
        `)
        .eq("company_id", companyData.id)
        .order("created_at", { ascending: false });

      if (bookingsError) throw bookingsError;

      // Map the data to include service_name and address
      const mappedBookings = (bookingsData || []).map(booking => ({
        ...booking,
        service_name: booking.services?.name || 'Unknown Service',
        service_duration_minutes: booking.services?.duration_minutes ?? null,
        address: booking.service_addresses
          ? `${booking.service_addresses.address_line1}${booking.service_addresses.address_line2 ? `, ${booking.service_addresses.address_line2}` : ''}, ${booking.service_addresses.city} ${booking.service_addresses.postal_code}`
          : (booking as any)?.service_address_text
            ? String((booking as any)?.service_address_text)
            : 'Address not available'
      }));

      setBookings(mappedBookings);

      // Auto-assign labor_minutes_est from service duration if missing
      try {
        const updates = (bookingsData || [])
          .filter((b: any) => !b.labor_minutes_est && b.services?.duration_minutes)
          .map((b: any) => (supabase as any).from('bookings').update({ labor_minutes_est: b.services.duration_minutes } as any).eq('id', b.id));
        if (updates.length) await Promise.allSettled(updates as any);
      } catch (_) {}

      // Calculate stats (earnings from completed bookings)
      const totalEarnings = bookingsData
        ?.filter(b => b.status === "completed")
        ?.reduce((sum, b) => sum + parseFloat(b.total_amount.toString()), 0) || 0;

      // Pending vendor remittances: from payments table (cash + invoice commission; Settle now includes both)
      let pendingPayments = 0;
      let pendingFromCash = 0;
      let pendingFromInvoice = 0;
      try {
        const { data: payRows } = await (supabase as any)
          .from('payments')
          .select('amount_cents,method')
          .eq('company_id', companyData.id)
          .eq('direction', 'vendor_to_platform')
          .in('status', ['pending_vendor_remit','requires_payment_method']);
        (payRows || []).forEach((r: any) => {
          const cents = r.amount_cents || 0;
          pendingPayments += cents;
          if (r.method === 'invoice') pendingFromInvoice += cents;
          else pendingFromCash += cents; // cash or any other
        });
        pendingPayments /= 100;
        pendingFromCash /= 100;
        pendingFromInvoice /= 100;
      } catch {}

      const totalBookings = bookingsData?.length || 0;
      const completedBookings = bookingsData?.filter(b => b.status === "completed").length || 0;

      setStats({
        totalEarnings,
        pendingPayments,
        pendingFromCash,
        pendingFromInvoice,
        totalBookings,
        completedBookings,
        averageRating: companyData.rating || 0,
        totalReviews: companyData.total_reviews || 0,
      });

    } catch (error) {
      toast({
        title: t("vendor.toast.error"),
        description: (error as any).message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Refresh when returning from vendor settle flow or Stripe Connect (return URL must hit /company/dashboard)
  useEffect(() => {
    const remit = searchParams.get('remit');
    if (remit === 'success' || remit === 'cancel') {
      checkAuthAndFetchData();
    }
    const stripe = searchParams.get('stripe');
    if (stripe === 'connected' || stripe === 'reconnect' || stripe === 'return') {
      checkAuthAndFetchData();
      const next = new URLSearchParams(searchParams);
      next.delete('stripe');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Da /for-businesses: scroll al blocco piano (upgrade PRO/SCALE senza loop di redirect).
  // FASE 1 (no Partita IVA): se SUBSCRIPTIONS_ENABLED=false, ignoriamo il param e lo strippiamo dall'URL
  // per evitare ancore visive verso una sezione nascosta.
  useEffect(() => {
    const up = searchParams.get('b2b_upgrade');
    if (up !== 'scale' && up !== 'pro') return;
    if (!SUBSCRIPTIONS_ENABLED) {
      const next = new URLSearchParams(searchParams);
      next.delete('b2b_upgrade');
      setSearchParams(next, { replace: true });
      return;
    }
    const t = window.setTimeout(() => {
      try {
        document.getElementById('vendor-b2b-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {}
    }, 100);
    const next = new URLSearchParams(searchParams);
    next.delete('b2b_upgrade');
    setSearchParams(next, { replace: true });
    return () => window.clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateBookingStatus = async (bookingId: string, newStatus: string) => {
    try {
      if (!(await requireAAL2())) return;
      // Collect reasons when required
      let updatePayload: any = { status: newStatus };
      const nowIso = new Date().toISOString();
      if (newStatus === "confirmed") {
        updatePayload.approval_status = "approved";
        updatePayload.vendor_responded_at = nowIso;
      }
      if (newStatus === 'in_progress') {
        updatePayload.start_at = nowIso;
      }
      if (newStatus === 'completed') {
        updatePayload.finish_at = nowIso;
      }
      if (newStatus === 'cancelled') {
        // Open drawer to collect cancel reason
        setCancelDraft({ bookingId, reason: '' });
        setCancelEstimate(null);
        setCancelDrawerOpen(true);
        // Best-effort: show refund estimate before confirming cancellation (policy 48h is in RPCs)
        try {
          const { data, error } = await (supabase as any).rpc('test_fee_retention_logic', { p_booking_id: bookingId });
          if (!error) {
            const row = Array.isArray(data) ? data[0] : data;
            if (row) {
              const toNum = (v: any) => {
                const n = typeof v === 'number' ? v : Number(v);
                return Number.isNaN(n) ? undefined : n;
              };
              setCancelEstimate({
                total_amount: toNum(row.total_amount),
                customer_fee_cents: row.customer_fee_cents ?? null,
                hours_until_service: toNum(row.hours_until_service),
                within48: !!row.within48,
                calculated_retained_cents: toNum(row.calculated_retained_cents),
                calculated_refund_cents: toNum(row.calculated_refund_cents),
              });
            }
          }
        } catch {
          // ignore
        }
        return;
      }

      const { error } = await supabase
        .from("bookings")
        .update(updatePayload)
        .eq("id", bookingId);

      if (error) throw error;

      // Update local state
      setBookings(prev => prev.map(booking => (
        booking.id === bookingId
          ? {
              ...booking,
              status: newStatus as any,
              approval_status: updatePayload.approval_status ?? (booking as any).approval_status,
              vendor_responded_at: updatePayload.vendor_responded_at ?? (booking as any).vendor_responded_at,
              start_at: updatePayload.start_at || (booking as any).start_at,
              finish_at: updatePayload.finish_at || (booking as any).finish_at,
            }
          : booking
      )));

      // Email notifications to customer based on status
      try {
        const { data: bk } = await (supabase as any)
          .from('bookings')
          .select('id, booking_number, customer_email, customer_name, scheduled_date, scheduled_time, total_amount')
          .eq('id', bookingId)
          .maybeSingle();
        const local = bookings.find((b: any) => b.id === bookingId) as any;
        const to = bk?.customer_email;
        // Build ICS (one-off 2h slot)
        const icsBuild = () => {
          if (!bk?.scheduled_date) return null;
          const start = new Date(`${bk.scheduled_date}T${(bk.scheduled_time || '09:00')}:00`);
          const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
          const dt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
          const ics = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Halomora//Bookings//EN',
            'BEGIN:VEVENT',
            `UID:${bookingId || `${Date.now()}@halomora`}`,
            `DTSTAMP:${dt(new Date())}`,
            `DTSTART:${dt(start)}`,
            `DTEND:${dt(end)}`,
            `SUMMARY:${local?.service_name || 'Service'} - ${(company as any)?.company_name || 'Vendor'}`,
            `DESCRIPTION:${local?.service_name || 'Service'} with ${(company as any)?.company_name || 'Vendor'}`,
            'END:VEVENT',
            'END:VCALENDAR'
          ].join('\\r\\n');
          const base64 = typeof btoa !== 'undefined' ? btoa(ics) : Buffer.from(ics).toString('base64');
          return { filename: `booking-${bookingId || 'event'}.ics`, base64 };
        };

        const payload = {
          customer_name: bk?.customer_name || '',
          company_name: (company as any)?.company_name || '',
          service_name: local?.service_name || '',
          booking_date: bk?.scheduled_date || '',
          booking_time: bk?.scheduled_time || '',
          booking_total: formatCurrency(Number(bk?.total_amount || 0)),
          total_amount: Number(bk?.total_amount || 0),
          booking_number: bk?.booking_number || '',
          manage_link: window.location.origin + '/profile',
          reschedule_link: window.location.origin + '/profile',
          cancel_link: window.location.origin + '/profile',
        };
        const base = { booking_id: bookingId, to_email: to, payload };
        if (to) {
          if (newStatus === 'confirmed') {
            const ics = icsBuild();
            await fetch('/api/notifications?action=enqueue', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...base, template: 'booking_confirmed', sendNow: true, ics_filename: ics?.filename, ics_content_base64: ics?.base64 })
            });
            // Schedule reminder T-24h
            try {
              const when = bk?.scheduled_date ? new Date(`${bk.scheduled_date}T${(bk.scheduled_time || '09:00')}:00`) : null;
              if (when) {
                const sched = new Date(when.getTime() - 24 * 60 * 60 * 1000).toISOString();
                const ics2 = icsBuild();
                await fetch('/api/notifications?action=enqueue', {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ ...base, template: 'reminder_24h', scheduled_at: sched, ics_filename: ics2?.filename, ics_content_base64: ics2?.base64 })
                });
              }
            } catch {}
          } else if (newStatus === 'completed') {
            await fetch('/api/notifications?action=enqueue', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...base, template: 'booking_completed', sendNow: true })
            });
          }
        }
      } catch {}

      // If confirming a recurring parent, expand future occurrences now
      try {
        if (newStatus === 'confirmed') {
          const { data: parent } = await (supabase as any)
            .from('bookings')
            .select('id, scheduled_date, recurrence_frequency, recurrence_end_date, series_id')
            .eq('id', bookingId)
            .maybeSingle();
          if (parent && parent.recurrence_frequency && parent.recurrence_end_date) {
            const freq = String(parent.recurrence_frequency) as 'weekly'|'biweekly'|'monthly';
            const start = new Date(parent.scheduled_date);
            const end = new Date(parent.recurrence_end_date);
            // Generate dates after the first
            const toISO = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset()*60000).toISOString().slice(0,10);
            let cursor = new Date(start);
            if (freq === 'weekly') cursor.setDate(cursor.getDate() + 7);
            else if (freq === 'biweekly') cursor.setDate(cursor.getDate() + 14);
            else cursor.setMonth(cursor.getMonth() + 1);
            const creates: Promise<any>[] = [];
            while (cursor <= end) {
              const occPayload: any = {
                parent_booking_id: bookingId,
                series_id: parent.series_id || bookingId,
                scheduled_date: toISO(cursor),
                scheduled_time: (bookings.find(b => b.id === bookingId) as any)?.scheduled_time,
                status: 'confirmed',
              };
              creates.push((supabase as any).rpc('create_public_booking_by_ids', {
                p_company_id: company?.id,
                p_service_id: (bookings.find(b => b.id === bookingId) as any)?.service_id,
                p_payload: occPayload,
              }));
              if (freq === 'weekly') cursor.setDate(cursor.getDate() + 7);
              else if (freq === 'biweekly') cursor.setDate(cursor.getDate() + 14);
              else cursor.setMonth(cursor.getMonth() + 1);
            }
            if (creates.length) await Promise.allSettled(creates);
            // Refresh list
            checkAuthAndFetchData();
          }
        }
      } catch (_) {}

      // Fire calendar sync
      try {
        console.log('Calling calendar sync for status update:', bookingId, newStatus);
        const response = await fetchJson("/api/calendar/sync-booking", {
          method: "POST",
          body: JSON.stringify({ booking_id: bookingId })
        });
        console.log('Calendar sync response (status update):', response);
      } catch (error) {
        console.error('Calendar sync error (status update):', error);
      }

toast({ title: t("vendor.toast.status_updated") });
    } catch (error) {
toast({ title: t("vendor.toast.error"), description: (error as any).message, variant: "destructive" });
    }
  };

  const confirmCancel = async () => {
    try {
      if (!(await requireAAL2())) return;
      const reason = (cancelDraft.reason || '').trim();
      if (!reason) {
toast({ title: t("vendor.toast.reason_required"), description: t("vendor.toast.cancel_reason"), variant: "destructive" });
        return;
      }
      const doDecline = async (accessToken: string) => {
        const resp = await fetch('/api/ops?action=vendor_decline_booking', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({ booking_id: cancelDraft.bookingId, reason, access_token: accessToken })
        });
        return { resp, json: await resp.json().catch(() => ({})) };
      };

      await supabase.auth.refreshSession();
      let { data: { session } } = await supabase.auth.getSession();
      let token = session?.access_token;
      if (!token) {
toast({ title: t("vendor.toast.session_unavailable"), description: t("vendor.toast.session_relogin"), variant: "destructive" });
        return;
      }

      let { resp, json } = await doDecline(token);
      if (resp.status === 401 && !json?.ok) {
        await supabase.auth.refreshSession();
        const next = await supabase.auth.getSession();
        token = next?.data?.session?.access_token;
        if (token) {
          const retry = await doDecline(token);
          resp = retry.resp;
          json = retry.json;
        }
      }

      if (!resp.ok || json?.ok !== true) {
        const msg = resp.status === 401
? t('vendor.toast.session_expired')
          : (json?.error || json?.message || t('vendor.toast.cancel_failed'));
        throw new Error(msg);
      }
      setBookings(prev => prev.map(b => b.id === cancelDraft.bookingId ? { ...b, status: 'cancelled' as any, approval_status: 'declined' as any } : b));
      try { await fetch('/api/calendar/sync-booking', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ booking_id: cancelDraft.bookingId }) }); } catch {}
      setCancelDrawerOpen(false);
      setCancelDraft({ bookingId: '', reason: '' });
      setCancelEstimate(null);
toast({ title: t("vendor.toast.cancelled") });
    } catch (e: any) {
toast({ title: t("vendor.toast.error"), description: e.message || t("vendor.toast.cancel_failed"), variant: "destructive" });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-CA', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    // If it's a slot like "08:00 - 12:00", return as-is; otherwise format HH:MM
    if (time.includes('-')) return time;
    return new Date(`2000-01-01 ${time}`).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
  };

  // Convert a Date to local YYYY-MM-DD (no timezone shift)
  const toLocalYmd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const handleReschedule = async (bookingId: string, newDate: Date, newTime: string) => {
    setResDraft({ bookingId, newDate, newTime, reason: "" });
    setResDrawerOpen(true);
  };

  const confirmReschedule = async () => {
    try {
      if (!(await requireAAL2())) return;
      if (!resDraft.newDate || !resDraft.newTime) return;
      const isoDate = toLocalYmd(resDraft.newDate);
      const reason = (resDraft.reason || "").trim();
      if (!reason) {
toast({ title: t("vendor.toast.reason_required"), description: t("vendor.toast.reschedule_reason"), variant: "destructive" });
        return;
      }
      const { error } = await supabase
        .from("bookings")
        .update({ scheduled_date: isoDate, scheduled_time: resDraft.newTime, reschedule_reason: reason })
        .eq("id", resDraft.bookingId);
      if (error) throw error;

      setBookings(prev => prev.map(b => b.id === resDraft.bookingId ? { ...b, scheduled_date: isoDate as any, scheduled_time: resDraft.newTime as any } : b));

      try {
        await fetch("/api/calendar/sync-booking", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ booking_id: resDraft.bookingId }) });
      } catch {}

      // Notify customer (in-app + email)
      try {
        // Load booking details to reach customer
        const { data: b } = await (supabase as any)
          .from('bookings')
          .select('id, customer_id, customer_email, services(name), companies(company_name)')
          .eq('id', resDraft.bookingId)
          .maybeSingle();
        if (b) {
          // In-app notification to customer's auth user
          try {
            const { data: prof } = await (supabase as any)
              .from('profiles')
              .select('user_id')
              .eq('id', b.customer_id)
              .maybeSingle();
            if (prof?.user_id) {
await fetch('/api/notifications?action=push', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: prof.user_id, title: t('vendor.notify.rescheduled_title'), body: tx('vendor.notify.rescheduled_body', { date: isoDate, time: resDraft.newTime }) })});
            }
          } catch {}
          // Email notification
          if (b.customer_email) {
            try {
              await fetch('/api/notifications?action=enqueue', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  template: 'booking_rescheduled',
                  booking_id: resDraft.bookingId,
                  to_email: b.customer_email,
                  payload: {
                    customer_name: '',
                    company_name: b.companies?.company_name || '',
                    service_name: b.services?.name || 'Servizio',
                    booking_date: isoDate,
                    booking_time: resDraft.newTime,
                    reschedule_link: window.location.origin + '/profile'
                  },
                  sendNow: true,
                  language: 'it'
                })
              });
            } catch {}
          }
        }
      } catch {}

      setResDrawerOpen(false);
      setResDraft({ bookingId: "", newDate: null, newTime: "", reason: "" });
toast({ title: t("vendor.toast.rescheduled") });
      // Refresh server data so CalendarView and other widgets pick up changes
      await checkAuthAndFetchData();
    } catch (error) {
toast({ title: t("vendor.toast.error"), description: (error as any).message, variant: "destructive" });
    }
  };

  const setNow = () => new Date().toISOString();

  const refreshCalendarStatus = async () => {
    try {
      const { data: calAcc } = await (supabase as any)
        .from("calendar_accounts")
        .select("id, account_email, provider, access_token, token_expires_at")
        .eq("profile_id", (company as any)?.profile_id)
        .eq("provider", "google")
        .maybeSingle();
      
      // Check if token is valid (not expired)
      let isTokenValid = false;
      if (calAcc && calAcc.access_token && calAcc.token_expires_at) {
        const now = new Date();
        const expiresAt = new Date(calAcc.token_expires_at);
        isTokenValid = expiresAt > now;
      }
      
      setCalendarAccount(calAcc || null);
      setShowGoogleConnectBanner(!calAcc || !isTokenValid);
      
      toast({
title: t("vendor.toast.calendar_refreshed"),
        description: isTokenValid ? t("vendor.toast.calendar_active") : t("vendor.toast.calendar_reconnect"),
      });
    } catch (error) {
      toast({
title: t("vendor.toast.error"),
        description: t("vendor.toast.calendar_refresh_failed"),
        variant: "destructive",
      });
    }
  };

  const setBookingTimestamp = async (bookingId: string, field: 'assigned_at' | 'depart_at' | 'arrive_at' | 'start_at' | 'finish_at') => {
    try {
      if (!(await requireAAL2())) return;
      const payload: any = {};
      payload[field] = setNow();
      const { error } = await supabase.from('bookings').update(payload).eq('id', bookingId);
      if (error) throw error;
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...payload } as any : b));
      await checkAuthAndFetchData();
      toast({ title: tx('vendor.toast.field_set', { field: field.replace('_', ' ') }) });
    } catch (e: any) {
toast({ title: t('vendor.toast.error'), description: e.message || t('vendor.toast.timestamp_failed'), variant: 'destructive' });
    }
  };

  const setBookingMetrics = async (bookingId: string) => {
    try {
      if (!(await requireAAL2())) return;
      const est = window.prompt('Labor minutes (estimated):');
      const act = window.prompt('Labor minutes (actual):');
      const travel = window.prompt('Travel minutes:');
      const materials = window.prompt('Materials cost (EUR):');
      const payload: any = {};
      if (est) payload.labor_minutes_est = parseInt(est, 10) || null;
      if (act) payload.labor_minutes_actual = parseInt(act, 10) || null;
      if (travel) payload.travel_minutes = parseInt(travel, 10) || null;
      if (materials) payload.materials_cost = parseFloat(materials);
      if (Object.keys(payload).length === 0) return;
      const { error } = await supabase.from('bookings').update(payload).eq('id', bookingId);
      if (error) throw error;
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...payload } as any : b));
      await checkAuthAndFetchData();
toast({ title: t('vendor.toast.metrics_saved') });
    } catch (e: any) {
toast({ title: t('vendor.toast.error'), description: e.message || t('vendor.toast.metrics_save_failed'), variant: 'destructive' });
    }
  };

  const addChecklistItem = async (bookingId: string) => {
    try {
      const item = window.prompt('Checklist item:');
      if (!item) return;
      const required = window.confirm('Mark as required?');
      const { error } = await (supabase as any).from('booking_checklists').insert({ booking_id: bookingId, item, required });
      if (error) throw error;
      await checkAuthAndFetchData();
toast({ title: t('vendor.toast.checklist_added') });
    } catch (e: any) {
toast({ title: t('vendor.toast.error'), description: e.message || t('vendor.toast.checklist_failed'), variant: 'destructive' });
    }
  };

  const reportIncident = async (bookingId: string) => {
    try {
      const code = window.prompt('Incident type code (e.g., DAMAGE, NO_SHOW):');
      if (!code) return;
      const note = window.prompt('Incident note (optional):') || null;
      const { data: type, error: typeErr } = await (supabase as any)
        .from('incident_types')
        .select('id, code')
        .eq('code', code)
        .maybeSingle();
      if (typeErr) throw typeErr;
      if (!type) {
toast({ title: t('vendor.toast.incident_type_missing'), description: t('vendor.toast.incident_seed'), variant: 'destructive' });
        return;
      }
      const { error } = await (supabase as any).from('booking_incidents').insert({ booking_id: bookingId, type_id: type.id, note });
      if (error) throw error;
      await checkAuthAndFetchData();
toast({ title: t('vendor.toast.incident_reported') });
    } catch (e: any) {
toast({ title: t('vendor.toast.error'), description: e.message || t('vendor.toast.incident_failed'), variant: 'destructive' });
    }
  };

  // Helpers for drawer actions
  const uploadPhoto = async (bookingId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    return new Promise<void>((resolve) => {
      input.onchange = async () => {
        try {
          const file = (input.files || [])[0];
          if (!file) return resolve();
          const type = window.prompt(t('vendor.ops.photo_prompt')) || 'evidence';
          const key = `${bookingId}/${Date.now()}-${file.name}`;
          const { error: upErr } = await (supabase as any).storage.from('booking-photos').upload(key, file, { upsert: true });
          if (upErr) throw upErr;
          // Store the storage path (key). We will generate signed URLs at view-time.
          const { error: insErr } = await (supabase as any).from('booking_photos').insert({ booking_id: bookingId, url: key, photo_type: type });
          if (insErr) throw insErr;
          await checkAuthAndFetchData();
toast({ title: t('vendor.toast.photo_uploaded') });
        } catch (e: any) {
toast({ title: t('vendor.toast.upload_failed'), description: e.message || t('vendor.toast.error'), variant: 'destructive' });
        } finally {
          resolve();
        }
      };
      input.click();
    });
  };

  const handleDeletePhoto = async (photoId: string, photoUrl: string) => {
if (!window.confirm(t('vendor.ops.delete_photo_confirm'))) return;
    
    try {
      // Delete from storage first
      const { error: storageErr } = await (supabase as any)
        .storage.from('booking-photos')
        .remove([photoUrl]);
      
      if (storageErr) {
        console.warn('Storage deletion failed:', storageErr);
        // Continue with database deletion even if storage fails
      }

      // Delete from database
      const { error: dbErr } = await (supabase as any)
        .from('booking_photos')
        .delete()
        .eq('id', photoId);

      if (dbErr) throw dbErr;

      // Refresh the photos list
      await checkAuthAndFetchData();
toast({ title: t('vendor.ops.photo_deleted') });
    } catch (e: any) {
      toast({ 
title: t('vendor.ops.delete_failed'), 
        description: e.message || t('vendor.ops.delete_failed_desc'), 
        variant: 'destructive' 
      });
    }
  };

  const saveMetrics = async (bookingId: string, m: { mEst?: string; mAct?: string; mTravel?: string; mMaterials?: string; }) => {
    try {
      if (!(await requireAAL2())) return;
      const payload: any = {};
      if (m.mEst) payload.labor_minutes_est = parseInt(m.mEst, 10) || null;
      if (m.mAct) payload.labor_minutes_actual = parseInt(m.mAct, 10) || null;
      if (m.mTravel) payload.travel_minutes = parseInt(m.mTravel, 10) || null;
      if (m.mMaterials) payload.materials_cost = parseFloat(m.mMaterials);
      if (Object.keys(payload).length === 0) return;
      const { error } = await supabase.from('bookings').update(payload).eq('id', bookingId);
      if (error) throw error;
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...payload } as any : b));
toast({ title: t('vendor.toast.metrics_saved') });
    } catch (e: any) {
toast({ title: t('vendor.toast.error'), description: e.message || t('vendor.toast.metrics_save_failed'), variant: 'destructive' });
    }
  };

  const addChecklist = async (bookingId: string, c: { item: string; required: boolean; }) => {
    try {
      if (!c.item) return;
      console.log('➕ Adding checklist item:', { bookingId, item: c.item, required: c.required });
      const { data: { session } } = await supabase.auth.getSession();
if (!session) { toast({ title: t('vendor.toast.not_signed_in'), variant: 'destructive' }); return; }
      const resp = await fetch('/api/ops?action=add_checklist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ booking_id: bookingId, item: c.item, required: c.required })
      });
      const j = await resp.json().catch(() => ({}));
      console.log('✅ Checklist insert result:', j);
      if (!resp.ok) throw new Error(j?.error || 'Failed to add checklist');
      // Optimistic UI: attach to current ops drawer view by refetching list if open
      if (opsOpen && opsBooking && opsBooking.id === bookingId) {
        try {
          const r = await fetch(`/api/ops?action=list_checklist&booking_id=${encodeURIComponent(bookingId)}`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
          const d = await r.json();
          (window as any).__lastChecklist = d?.items || [];
        } catch {}
      }
toast({ title: t('vendor.toast.checklist_added') });
    } catch (e: any) {
toast({ title: t('vendor.toast.error'), description: e.message || t('vendor.toast.checklist_failed'), variant: 'destructive' });
    }
  };

  const reportIncidentWith = async (bookingId: string, d: { code: string; note?: string | null; }) => {
    try {
      if (!d.code) return;
      const { data: type, error: typeErr } = await (supabase as any)
        .from('incident_types')
        .select('id, code')
        .eq('code', d.code)
        .maybeSingle();
      if (typeErr) throw typeErr;
      if (!type) {
toast({ title: t('vendor.toast.incident_type_missing'), description: t('vendor.toast.incident_seed'), variant: 'destructive' });
        return;
      }
      const { error } = await (supabase as any).from('booking_incidents').insert({ booking_id: bookingId, type_id: type.id, note: d.note || null });
      if (error) throw error;
toast({ title: t('vendor.toast.incident_reported') });
    } catch (e: any) {
toast({ title: t('vendor.toast.error'), description: e.message || t('vendor.toast.incident_failed'), variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">{t("vendor.dashboard.loading")}</div>;
  }

  if (!company) {
    return <div className="flex items-center justify-center min-h-screen">{t("vendor.dashboard.not_found")}</div>;
  }

  return (
    <div key={lang} className="min-h-screen app-page overflow-x-hidden">
      <Seo title={t("vendor.dashboard.seo.title")} noindex canonicalPath="/company/dashboard" />
      {StepUpDialog}
      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t('vendor.welcome')}, {company.company_name}
            </h1>
            <p className="text-gray-600">
              {t('vendor.manage')}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <LanguageSwitcher />
            <Link to="/faq" className="text-sm text-slate-600 underline underline-offset-4 hover:text-slate-950">
{t("vendor.dashboard.need_help")}
            </Link>
            <Button 
              variant="outline" 
              onClick={() => navigate("/company/profile")}
            >
              <Settings className="h-4 w-4 mr-2" />
              {t('vendor.settings')}
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  if (!(await requireAAL2())) return;
                  const month = new Date().toISOString().slice(0,7);
                  const { data: { session } } = await supabase.auth.getSession();
                  if (!session) { navigate('/auth'); return; }
                  const resp = await fetch(`/api/stripe?action=vendor_statement_csv&month=${encodeURIComponent(month)}`, {
                    method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}` }
                  });
                  const blob = await resp.blob();
                  if (!resp.ok) throw new Error('Download failed');
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `statement_${month}.csv`; a.click(); URL.revokeObjectURL(url);
                } catch (e:any) {
                  toast({ title: t("vendor.dashboard.export_failed"), description: e.message || t("vendor.toast.error"), variant: "destructive" });
                }
              }}
            >
{t("vendor.dashboard.download_statement")}
            </Button>
            <div className="w-full rounded-lg border border-slate-200/70 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={acceptedVendorPayoutTerms}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    const prev = acceptedVendorPayoutTerms;
                    const cid = (company as any)?.id as string | undefined;
                    if (!cid) return;
                    setAcceptedVendorPayoutTerms(next);
                    const { error } = await supabase
                      .from("companies")
                      .update({ vendor_payments_terms_accepted: next })
                      .eq("id", cid);
                    if (error) {
                      setAcceptedVendorPayoutTerms(prev);
                      toast({
title: t("vendor.dashboard.save_failed"),
                        description: error.message,
                        variant: "destructive",
                      });
                      return;
                    }
                    setCompany((c) => (c ? { ...(c as any), vendor_payments_terms_accepted: next } : null));
                  }}
                  className="mt-1"
                />
                <span className="space-y-1">
<span className="block font-medium text-slate-900">{t("vendor.dashboard.payout_terms_title")}</span>
<span className="block">{t("vendor.dashboard.payout_terms_1")}</span>
<span className="block">{t("vendor.dashboard.payout_terms_2")}</span>
<span className="block">{t("vendor.dashboard.payout_terms_3")}</span>
                  <span className="block">
{t("vendor.dashboard.payout_terms_4_prefix")}{" "}
                    <Link to="/legal/vendor-agreement" className="text-primary underline">
{t("vendor.dashboard.vendor_agreement")}
                    </Link>.
                  </span>
                </span>
              </label>
            </div>
            {/* Stripe Connect actions */}
            <Button
              variant="outline"
              disabled={!acceptedVendorPayoutTerms}
              onClick={async () => {
                try {
                  if (!(await requireAAL2())) return;
                  await startStripeOnboarding();
                } catch (e: any) {
toast({ title: t('vendor.toast.stripe'), description: e.message || t('vendor.toast.error'), variant: 'destructive' });
                }
              }}
            >
{t("vendor.dashboard.stripe_onboarding")}
            </Button>
            <Button
              variant="outline"
              disabled={!acceptedVendorPayoutTerms}
              onClick={async () => {
                try {
                  if (!(await requireAAL2())) return;
                  await openStripeDashboard();
                } catch (e: any) {
toast({ title: t('vendor.toast.stripe'), description: e.message || t('vendor.toast.error'), variant: 'destructive' });
                }
              }}
            >
{t("vendor.dashboard.stripe_dashboard")}
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                const url = `${window.location.origin}/c/${(company as any)?.slug || ''}`;
                navigator.clipboard.writeText(url);
                toast({ title: t('vendor.toast.link_copied'), description: t('vendor.toast.link_desc') });
              }}
            >
              {t('vendor.share_public')}
            </Button>
            <Button 
              onClick={() => navigate("/company/profile?tab=services")}
              variant="hero"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('vendor.add_service')}
            </Button>
          </div>
        </div>

        {/* Il mio piano (B2B subscription).
            FASE 1 (no Partita IVA): nascosta dietro SUBSCRIPTIONS_ENABLED. Il blocco e' integralmente
            preservato e verra' ripristinato in Fase 2. Vedere src/config/features.ts. */}
        {SUBSCRIPTIONS_ENABLED && (
        <Card id="vendor-b2b-plan" className="app-dark-panel scroll-mt-20">
          <CardHeader>
<CardTitle className="flex items-center gap-2 text-white"><Crown className="h-5 w-5 text-amber-300" /> {t("vendor.b2b.title")}</CardTitle>
            <CardDescription className="text-slate-300">{t("vendor.b2b.desc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {b2bSubscription ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="border-white/10 bg-white/10 text-sky-200 text-sm">{tx("vendor.b2b.plan_label", { plan: String(b2bSubscription.plan).toUpperCase() })}</Badge>
                  <span className="text-sm text-slate-300">
                    {b2bSubscription.expires_at
                    ? (b2bSubscription.cancelled_at
                        ? tx('vendor.b2b.until', { date: new Date(b2bSubscription.expires_at).toLocaleDateString() })
                        : tx('vendor.b2b.renew', { date: new Date(b2bSubscription.expires_at).toLocaleDateString() }))
                    : t('vendor.b2b.active')}
                  </span>
                </div>
                {b2bSubscription.stripe_customer_id ? (
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        const token = session?.access_token;
                        if (!token) {
                          toast({ title: t("vendor.toast.session_unavailable"), description: t("vendor.toast.session_relogin"), variant: "destructive" });
                          return;
                        }
                        const r = await fetch('/api/ops?action=b2b_billing_portal', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ company_id: (company as any)?.id || b2bSubscription?.company_id, access_token: token })
                        });
                        const js = await r.json().catch(() => ({}));
                        if (!r.ok) {
                          const msg = js?.message || js?.error || t('vendor.toast.error');
                          const title = msg === 'subscription_admin_grant' ? t('vendor.toast.admin_plan_title') : js?.error === 'stripe_portal_error' ? t('vendor.toast.stripe_portal_title') : js?.error === 'company_id_required' ? t('vendor.toast.missing_data') : t('vendor.toast.error');
                          toast({ title, description: msg, variant: 'destructive' });
                          return;
                        }
                        if (js?.url) window.location.href = js.url;
                      } catch (e: any) {
                        toast({ title: t("vendor.toast.error"), description: e?.message || t("vendor.toast.portal_open_failed"), variant: "destructive" });
                      }
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" /> {t("vendor.b2b.manage")}
                  </Button>
                  {String(b2bSubscription.plan).toLowerCase() === 'pro' && (
                    <Button
                      variant="hero"
                      onClick={async () => {
                        try {
                          const { data: { session } } = await supabase.auth.getSession();
                          const token = session?.access_token;
                          if (!token) {
                            toast({ title: t("vendor.toast.session_unavailable"), description: t("vendor.toast.session_relogin"), variant: "destructive" });
                            return;
                          }
                          const r = await fetch('/api/ops?action=b2b_subscription_checkout', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ company_id: (company as any)?.id, plan: 'scale', access_token: token })
                          });
                          const js = await r.json().catch(() => ({}));
                          if (!r.ok) {
                            toast({
                              title: t("vendor.toast.checkout_unavailable"),
                              description: js?.error || js?.message || t("vendor.b2b.checkout_scale_hint"),
                              variant: 'destructive',
                            });
                            return;
                          }
                          if (js?.url) window.location.href = js.url;
                        } catch (e: any) {
                          toast({ title: t("vendor.toast.error"), description: e?.message || t("vendor.toast.checkout_start_failed"), variant: "destructive" });
                        }
                      }}
                    >
{t("vendor.b2b.upgrade_scale")}
                    </Button>
                  )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-300">{t("vendor.b2b.admin_assigned")}</p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-slate-300">{t("vendor.b2b.no_plan")}</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        const token = session?.access_token;
                        if (!token) {
                          toast({ title: t("vendor.toast.session_unavailable"), description: t("vendor.toast.session_relogin"), variant: "destructive" });
                          return;
                        }
                        const r = await fetch('/api/ops?action=b2b_subscription_checkout', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ company_id: (company as any)?.id, plan: 'pro', access_token: token })
                        });
                        const js = await r.json().catch(() => ({}));
                        if (!r.ok) {
                          toast({ title: t("vendor.toast.error"), description: js?.error || js?.message || t("vendor.toast.checkout_unavailable"), variant: "destructive" });
                          return;
                        }
                        if (js?.url) window.location.href = js.url;
                      } catch (e: any) {
                        toast({ title: t("vendor.toast.error"), description: e?.message || t("vendor.toast.checkout_start_failed"), variant: "destructive" });
                      }
                    }}
                  >
{t("vendor.b2b.subscribe_pro")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        const token = session?.access_token;
                        if (!token) {
                          toast({ title: t("vendor.toast.session_unavailable"), description: t("vendor.toast.session_relogin"), variant: "destructive" });
                          return;
                        }
                        const r = await fetch('/api/ops?action=b2b_subscription_checkout', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ company_id: (company as any)?.id, plan: 'scale', access_token: token })
                        });
                        const js = await r.json().catch(() => ({}));
                        if (!r.ok) {
                          toast({ title: t("vendor.toast.error"), description: js?.error || js?.message || t("vendor.toast.checkout_unavailable"), variant: "destructive" });
                          return;
                        }
                        if (js?.url) window.location.href = js.url;
                      } catch (e: any) {
                        toast({ title: t("vendor.toast.error"), description: e?.message || t("vendor.toast.checkout_start_failed"), variant: "destructive" });
                      }
                    }}
                  >
{t("vendor.b2b.subscribe_scale")}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/for-businesses')}>
{t("vendor.b2b.ultra_quote")}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Vendor internal hold banner */}
        {internalHold && (
          <Card className="border-red-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
{t("vendor.dashboard.payments_blocked_title")}
              </CardTitle>
              <CardDescription>
                {tx("vendor.dashboard.payments_blocked_desc", { rate: (disputeRate * 100).toFixed(1) })}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Google Calendar integration banner */}
        {stripeReady === false && (
          <Card className="app-panel border-slate-200/70">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-950">
{t("vendor.dashboard.stripe_required_title")}
              </CardTitle>
              <CardDescription className="text-slate-600">
{tx("vendor.dashboard.stripe_required_desc", { reqs: (stripeRequirements || []).join(", ") || "—" })}
              </CardDescription>
              {stripeConnectHint && (
                <CardDescription className="text-amber-800 border border-amber-200/80 bg-amber-50/90 rounded-md px-3 py-2 mt-2">
                  {stripeConnectHint}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="hero" onClick={startStripeOnboarding} disabled={!acceptedVendorPayoutTerms}>
{t("vendor.dashboard.connect_stripe")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {GOOGLE_CALENDAR_ENABLED && showGoogleConnectBanner && (
          <Card className="app-panel border-slate-200/70">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-950">
                <Calendar className="h-4 w-4 text-sky-300" />
                {calendarAccount ?
                  t('vendor.google.reconnect_title') :
                  t('vendor.google.title')
                }
              </CardTitle>
              <CardDescription className="text-slate-600">
                {calendarAccount ? 
                  t('vendor.google.reconnect_desc') :
                  t('vendor.google.desc')
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => {
                    const next = "/company/dashboard";
                    const pid = (company as any).profile_id;
                    window.location.assign(`/api/oauth/google/start?profile_id=${encodeURIComponent(pid)}&next=${encodeURIComponent(next)}`);
                  }}
                  variant="hero"
                >
                  {calendarAccount ? 
                    t('vendor.google.reconnect') : 
                    t('vendor.google.connect')
                  }
                </Button>
                <Button
                  variant="outline"
                  onClick={refreshCalendarStatus}
                  className="text-sm"
                >
{t("vendor.dashboard.refresh_status")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="app-panel hover:shadow-strong">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">{t('vendor.kpi.total_earnings')}</CardTitle>
              <div className="w-8 h-8 bg-sky-50 rounded-lg flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-sky-300" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-950">{formatCurrency(stats.totalEarnings)}</div>
              <p className="text-xs text-slate-500 mt-1">
{tx("vendor.kpi.from_completed", { count: stats.completedBookings })}
              </p>
            </CardContent>
          </Card>

          <Card className="app-panel hover:shadow-strong">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">{t('vendor.kpi.total_due_halomora')}</CardTitle>
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="h-4 w-4 text-amber-300" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-950">{formatCurrency(stats.pendingPayments)}</div>
              <p className="text-xs text-slate-500 mt-1 space-y-0.5">
                {stats.pendingFromCash > 0 && <span className="block">{t('vendor.kpi.pending_settle')}: {formatCurrency(stats.pendingFromCash)}</span>}
                {stats.pendingFromInvoice > 0 && <span className="block">{t('vendor.kpi.commission_invoices')}: {formatCurrency(stats.pendingFromInvoice)}</span>}
{stats.pendingPayments === 0 && t("vendor.kpi.no_pending")}
              </p>
              <div className="mt-3">
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    if (!(await requireAAL2())) return;
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) { navigate('/auth'); return; }
                    const token = session.access_token;
                    const resp = await fetch('/api/stripe?action=vendor_settle', { 
                      method: 'POST', 
                      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ company_id: (company as any)?.id })
                    });
                    const js = await resp.json();
if (!resp.ok) throw new Error(js?.error || t('vendor.toast.settle_api_failed'));
                    if (js?.url) { window.location.assign(js.url); return; }
                    toast({ title: t("vendor.toast.balance"), description: js.amount_cents ? tx("vendor.toast.balance_amount", { amount: formatCurrency(js.amount_cents/100) }) : t("vendor.toast.no_balance") });
                  } catch (e: any) {
                    toast({ title: t("vendor.toast.settle_failed"), description: e.message || t("vendor.toast.error"), variant: "destructive" });
                  }
}}>{t("vendor.kpi.settle_balance")}</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="app-panel hover:shadow-strong">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">{t('vendor.kpi.total_bookings')}</CardTitle>
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Users className="h-4 w-4 text-emerald-300" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-950">{stats.totalBookings}</div>
              <p className="text-xs text-slate-500 mt-1">
{tx("vendor.kpi.completed_short", { count: stats.completedBookings })}
              </p>
            </CardContent>
          </Card>

          <Card className="app-panel hover:shadow-strong">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">{t('vendor.kpi.avg_rating')}</CardTitle>
              <div className="w-8 h-8 bg-violet-50 rounded-lg flex items-center justify-center">
                <Star className="h-4 w-4 text-violet-300" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-950">{stats.averageRating.toFixed(1)}</div>
              <p className="text-xs text-slate-500 mt-1">
{tx("vendor.kpi.from_reviews", { count: stats.totalReviews })}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="app-panel max-w-full">
          <Tabs
            value={(searchParams.get("tab") as string) || "bookings"}
            onValueChange={(v) => setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set("tab", v); return next; })}
            className="w-full"
          >
            <div className="border-b border-gray-200 px-4">
              <TabsList className="flex w-full gap-2 justify-start bg-transparent rounded-none p-0 py-2 pl-6 pr-6 overflow-x-auto overflow-y-hidden min-h-[2.75rem] min-w-0 [scrollbar-width:thin]">
                <TabsTrigger 
                  value="bookings" 
                  className="whitespace-nowrap shrink-0 px-4 py-2"
                >
                  <Calendar className="h-4 w-4 mr-2 inline-block" />
                  {t('vendor.tabs.bookings')}
                </TabsTrigger>
                <TabsTrigger 
                  value="calendar" 
                  className="whitespace-nowrap shrink-0 px-4 py-2"
                >
                  <CalendarDays className="h-4 w-4 mr-2 inline-block" />
                  {t('vendor.tabs.calendar')}
                </TabsTrigger>
                <TabsTrigger
                  value="tracker"
                  className="whitespace-nowrap shrink-0 px-4 py-2"
                >
                  <Layers className="h-4 w-4 mr-2 inline-block" />
                  {t('vendor.tabs.tracker')}
                </TabsTrigger>
                {QUOTES_ENABLED && (
                  <TabsTrigger
                    value="quotes"
                    className="whitespace-nowrap shrink-0 px-4 py-2"
                  >
                    <MessageSquare className="h-4 w-4 mr-2 inline-block" />
                    {t('vendor.tabs.quotes')}
                  </TabsTrigger>
                )}
                <TabsTrigger 
                  value="services" 
                  className="whitespace-nowrap shrink-0 px-4 py-2"
                >
                  <Wrench className="h-4 w-4 mr-2 inline-block" />
                  {t('vendor.tabs.services')}
                </TabsTrigger>
                <TabsTrigger 
                  value="pricing" 
                  className="whitespace-nowrap shrink-0 px-4 py-2"
                >
                  <TrendingUp className="h-4 w-4 mr-2 inline-block" />
                  {t('vendor.tabs.pricing')}
                </TabsTrigger>
                <TabsTrigger 
                  value="recurring" 
                  className="whitespace-nowrap shrink-0 px-4 py-2"
                >
                  <Repeat className="h-4 w-4 mr-2 inline-block" />
                  {t('vendor.tabs.recurring')}
                </TabsTrigger>
                <TabsTrigger 
                  value="analytics" 
                  className="whitespace-nowrap shrink-0 px-4 py-2"
                >
                  {isProUser ? (
                    <Crown className="h-4 w-4 mr-2 inline-block text-yellow-500" />
                  ) : (
                    <Lock className="h-4 w-4 mr-2 inline-block" />
                  )}
                  {t('vendor.tabs.analytics')}
                </TabsTrigger>
                <TabsTrigger 
                  value="invoices" 
                  className="whitespace-nowrap shrink-0 px-4 py-2"
                >
                  <FileText className="h-4 w-4 mr-2 inline-block" />
                  {t('vendor.tabs.invoices')}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6">
              <TabsContent value="bookings" className="space-y-6">
                {!!company?.id && <BulkOrdersPanel companyId={company.id} />}
                <BookingInbox 
                  bookings={bookings}
                  onAccept={(id) => updateBookingStatus(id, 'confirmed')}
                  onDecline={(id) => updateBookingStatus(id, 'cancelled')}
                  onOpenOps={(booking) => {
                    setOpsBooking(booking);
                    setMEst(((booking as any).labor_minutes_estimated ?? '') + '');
                    setMAct(((booking as any).labor_minutes_actual ?? '') + '');
                    setMTravel(((booking as any).travel_minutes ?? '') + '');
                    setMMaterials(((booking as any).materials_cost ?? '') + '');
                    setOpsOpen(true);
                  }}
                />
                <Card>
                  <CardHeader>
<CardTitle>{t("vendor.bookings.recent_title")}</CardTitle>
                    <CardDescription>
{t("vendor.bookings.recent_desc")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {bookings.length === 0 ? (
                      <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium">{t('vendor.no_bookings')}</h3>
                <p className="text-muted-foreground">
                  {t('vendor.no_bookings_desc')}
                </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {bookings.map((booking) => (
                          <div key={booking.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="space-y-1">
                              <div className="flex items-center gap-3">
                                <Badge className={getStatusColor(booking.status)}>
                                  {booking.status.replace('_', ' ')}
                                </Badge>
                                <span className="font-medium">{booking.booking_number}</span>
                                {(booking as any)?.series_id && (
<Badge variant="outline">{t("vendor.bookings.recurring_badge")}</Badge>
                                )}
                              </div>
                              <h4 className="font-medium">{booking.service_name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {booking.customer_name} • {booking.customer_phone}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(booking.scheduled_date)} at {formatTime(booking.scheduled_time)}
                              </p>
                            </div>
                            <div className="text-right space-y-2">
                              {(() => {
                                const _svc = Number(booking.total_amount || 0);
                                const _feeC = Math.max(0, Number((booking as any).customer_fee_cents || 0));
                                const _vc = !!(booking as any).pricing_snapshot?.vendor_covers_customer_fee;
                                const _custPaid = _svc + (_vc ? 0 : _feeC / 100);
                                return (
                                  <div className="text-lg font-bold">
                                    {formatCurrency(_custPaid)}
                                    {_feeC > 0 && (
                                      <div className="text-[10px] font-normal text-muted-foreground">
                                        Servizio {formatCurrency(_svc)} {_vc ? '(fee coperta da te)' : `+ fee ${formatCurrency(_feeC/100)}`}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {((booking as any).platform_fee_cents || (booking as any).customer_fee_cents || (booking as any).payout_cents) && (
                                <div className="text-xs text-muted-foreground space-y-1 text-right">
                                  {typeof (booking as any).platform_fee_cents === 'number' && (
                                    <div>{t('vendor.fees.platform')}: {formatCurrency((((booking as any).platform_fee_cents)/100))}</div>
                                  )}
                                  {typeof (booking as any).customer_fee_cents === 'number' && (
                                    <div>{t('vendor.fees.customer')}: {formatCurrency((((booking as any).customer_fee_cents)/100))}</div>
                                  )}
                                  {typeof (booking as any).processor_fee_cents === 'number' && (
                                    <div>{t('vendor.fees.processor')}: {formatCurrency((((booking as any).processor_fee_cents)/100))}</div>
                                  )}
                                  {typeof (booking as any).payout_cents === 'number' && (
                                    <div>{t('vendor.fees.payout')}: {formatCurrency((((booking as any).payout_cents)/100))}</div>
                                  )}
                                </div>
                              )}
                              <div className="flex gap-2 flex-wrap justify-end">
                                <Button size="sm" variant="secondary" onClick={() => navigate(`/booking/${booking.id}`)}>
                                  Dettagli
                                </Button>
                                <Button size="sm" variant="secondary" onClick={() => {
                                  setOpsBooking(booking);
                                  setMEst(((booking as any).labor_minutes_est ?? '') + '');
                                  setMAct(((booking as any).labor_minutes_actual ?? '') + '');
                                  setMTravel(((booking as any).travel_minutes ?? '') + '');
                                  setMMaterials(((booking as any).materials_cost ?? '') + '');
                                  setOpsOpen(true);
                                }}>Ops</Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="calendar" className="space-y-4">
                <AvailabilityEditor onSave={() => toast({ title: t("vendor.availability.saved_title"), description: t("vendor.availability.saved_desc") })} />
                <CalendarView 
                  bookings={bookings}
                  onStatusUpdate={updateBookingStatus}
                  onReschedule={handleReschedule}
                />
              </TabsContent>

              <TabsContent value="tracker" className="space-y-4">
                {company?.id ? (
                  <ExternalBookingsTracker
                    companyId={company.id}
                    bookings={bookings}
                    services={services.map((s: any) => ({ id: String(s.id), name: String(s.name || '') }))}
                  />
                ) : (
                  <div className="text-sm text-slate-500">{t('vendor.tracker.no_company')}</div>
                )}
              </TabsContent>

              {QUOTES_ENABLED && (
                <TabsContent value="quotes" className="space-y-4">
                  <QuoteRequestManager
                    companyId={company?.id || ""}
                    onQuoteSent={(requestId, quote) => {
                      toast({
                        title: t("vendor.quotes.sent_title"),
                        description: t("vendor.quotes.sent_desc"),
                      });
                    }}
                    onStatusUpdate={(requestId, status) => {
                      toast({
                        title: t("vendor.quotes.status_updated"),
                        description: tx("vendor.quotes.status_changed_desc", { status }),
                      });
                    }}
                  />
                </TabsContent>
              )}

              <TabsContent value="services" className="space-y-4">
                {(searchParams.get("edit") || searchParams.get("new") === "1") && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/company/profile?tab=services")}
                  >
                    ← {t("vendor.services.back_to_profile")}
                  </Button>
                )}
                <Card>
                  <CardHeader>
                    <CardTitle>{t("vendor.services.existing_title")}</CardTitle>
                    <CardDescription>
                      {t("vendor.services.existing_desc")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {services.filter((s: any) => !s.is_deleted).length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t("vendor.services.none")}</p>
                    ) : (
                      <ul className="space-y-2">
                        {services
                          .filter((s: any) => !s.is_deleted)
                          .map((s: any) => (
                            <li
                              key={s.id}
                              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3"
                            >
                              <span className="font-medium">{s.name}</span>
                              <Button
                                type="button"
                                variant={editingService?.id === s.id ? "secondary" : "outline"}
                                size="sm"
                                onClick={() => setEditingService((cur: any) => (cur?.id === s.id ? null : s))}
                              >
                                {editingService?.id === s.id
                                  ? t("vendor.services.cancel_edit")
                                  : t("vendor.services.edit")}
                              </Button>
                            </li>
                          ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>

                {editingService ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("vendor.services.editing_label")}:{" "}
                      <span className="text-foreground">{editingService.name}</span>
                    </p>
                    <ServiceBuilder
                      key={editingService.id}
                      categories={categories}
                      existingService={mapDbServiceForServiceBuilder(editingService)}
                      onServiceCreate={async (service) => {
                        try {
                          if (!company) throw new Error("Company not loaded");
                          if (!(await requireAAL2())) return;
                          const { data: { session } } = await supabase.auth.getSession();
                          if (!session?.access_token) throw new Error("Not authenticated");
                          const resp = await fetch("/api/company/services/add", {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${session.access_token}`,
                            },
                            body: JSON.stringify({
                              company_id: company.id,
                              service_id: editingService.id,
                              service: {
                                name: service.name,
                                description: service.description,
                                category_id: service.category_id,
                                base_price: service.base_price,
                                pricing_type: service.pricing_type,
                                duration_minutes: service.duration_minutes,
                                included_items: service.included_items || [],
                                benefit_items: service.benefit_items || [],
                                allowed_service_types: service.allowed_service_types || [],
                                pricing_rules: service.pricing_rules || {},
                                booking_policy: service.booking_policy || {},
                                accepted_payment_methods: service.accepted_payment_methods || ["card", "cash"],
                                vendor_covers_customer_fee: !!service.vendor_covers_customer_fee,
                                display_fee_disclaimer: !!service.display_fee_disclaimer,
                                tags: Array.isArray(editingService.tags) ? editingService.tags : [],
                              },
                            }),
                          });
                          if (!resp.ok) {
                            const j = await resp.json().catch(() => ({}));
                            throw new Error(j?.error || "Failed to update service");
                          }
                          const { data } = await supabase
                            .from("services")
                            .select("*, service_categories(name)")
                            .eq("company_id", company.id)
                            .order("created_at", { ascending: false });
                          setServices(data || []);
                          setEditingService(null);
                          toast({
                            title: t("vendor.services.updated_title"),
                            description: t("vendor.services.updated_desc"),
                          });
                        } catch (e: any) {
                          toast({
                            title: t("vendor.services.update_failed"),
description: e.message || t("vendor.toast.error"),
                            variant: "destructive",
                          });
                        }
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium">{t("vendor.services.create_new")}</p>
                    <ServiceBuilder
                      key="new-service"
                      categories={categories}
                      onServiceCreate={async (service) => {
                        try {
                          if (!company) throw new Error("Company not loaded");
                          if (!(await requireAAL2())) return;
                          const { data: { session } } = await supabase.auth.getSession();
                          if (!session?.access_token) throw new Error("Not authenticated");
                          const resp = await fetch('/api/company/services/add', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                            body: JSON.stringify({
                              company_id: company.id,
                              services: [service]
                            })
                          });
                          if (!resp.ok) {
                            const j = await resp.json().catch(() => ({}));
                            throw new Error(j?.error || 'Failed to add service');
                          }
                          const { data } = await supabase
                            .from('services')
                            .select('*, service_categories(name)')
                            .eq('company_id', company.id)
                            .order('created_at', { ascending: false });
                          setServices(data || []);
toast({ title: t("vendor.services.created_title"), description: t("vendor.services.created_desc") });
                        } catch (e: any) {
toast({ title: t("vendor.services.create_failed"), description: e.message || t("vendor.toast.error"), variant: "destructive" });
                        }
                      }}
                    />
                  </>
                )}
                <SeasonalOfferBuilder 
                  companyId={(company as any)?.id}
                  services={services.map((s:any) => ({ id: s.id || s.name, name: s.name }))}
                  onCreate={() => {
toast({ title: t("vendor.offers.published_title"), description: t("vendor.offers.published_desc") });
                    // Trigger a local refresh by updating a state key
                    try { setOffersRefreshKey((k) => k + 1); } catch {}
                  }}
                />
                <Card>
                  <CardHeader>
<CardTitle>{t("vendor.offers.title")}</CardTitle>
<CardDescription>{t("vendor.offers.desc")}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <OffersList companyId={company?.id || ''} services={services} refreshKey={offersRefreshKey} requireAAL2={requireAAL2} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="recurring" className="space-y-4">
                {company && (
                  <RecurringBookingManager companyId={(company as any).id} />
                )}
              </TabsContent>

              <TabsContent value="pricing" className="space-y-4">
                <SmartPriceEstimator 
                  services={services}
                  onSaveRule={(rule) => {
                    toast({
title: t("vendor.pricing.rule_saved"),
                      description: t("vendor.pricing.rule_saved_desc"),
                    });
                  }}
                  onTestEstimate={(estimate) => {
                    toast({
title: t("vendor.pricing.estimate_title"),
                      description: tx("vendor.pricing.estimate_desc", { min: formatCurrency(estimate.minPrice), max: formatCurrency(estimate.maxPrice) }),
                    });
                  }}
                />
              </TabsContent>

              <TabsContent value="recurring" className="space-y-4">
                <RecurringBookingManager companyId={company?.id || ''} />
              </TabsContent>

              <TabsContent value="analytics" className="space-y-6">
                {/* UTM Analytics - Available for all users */}
                <div>
<h3 className="text-lg font-semibold mb-4">{t("vendor.analytics.marketing")}</h3>
                  <UtmAnalytics companyId={company?.id || ''} />
                </div>

                {/* Advanced Analytics - PRO only.
                    FASE 1: la lock-screen "Upgrade to PRO" e' nascosta perche' la subscription PRO e' disabilitata.
                    Quando SUBSCRIPTIONS_ENABLED=true torna visibile l'esperienza originale. */}
                <div>
<h3 className="text-lg font-semibold mb-4">{t("vendor.analytics.advanced")}</h3>
                  {isProUser ? (
                    <CompanyAdvancedAnalytics companyId={company?.id} />
                  ) : !SUBSCRIPTIONS_ENABLED ? (
                    <Card className="app-dark-panel border border-white/10">
                      <CardContent className="py-10 px-6 text-center text-slate-200">
                        <Lock className="h-10 w-10 text-slate-400 mx-auto mb-3" />
<h4 className="text-lg font-semibold text-white mb-2">{t("vendor.analytics.coming_soon_title")}</h4>
                        <p className="text-sm text-slate-300 max-w-md mx-auto">
{t("vendor.analytics.coming_soon_desc")}
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="app-dark-panel border border-white/10">
                      <CardContent className="flex flex-col items-center justify-center py-16 px-8 text-center">
                        <div className="mb-6">
                          <Lock className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                          <h3 className="text-2xl font-bold text-white mb-2">
{t("vendor.analytics.pro_title")}
                          </h3>
                          <p className="text-slate-300 mb-6 max-w-md">
{t("vendor.analytics.pro_desc")}
                          </p>
                        </div>
                        
                        <div className="app-dark-metric p-6 mb-6 max-w-md">
                          <div className="flex items-center gap-2 mb-3">
                            <Crown className="h-5 w-5 text-amber-300" />
<h4 className="font-semibold text-white">{t("vendor.analytics.pro_plan")}</h4>
                          </div>
                          <ul className="text-sm text-slate-200 space-y-2 text-left">
                            <li className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-amber-300 rounded-full"></div>
{t("vendor.analytics.pro_feature_1")}
                            </li>
                            <li className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-amber-300 rounded-full"></div>
{t("vendor.analytics.pro_feature_2")}
                            </li>
                            <li className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-amber-300 rounded-full"></div>
{t("vendor.analytics.pro_feature_3")}
                            </li>
                            <li className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-amber-300 rounded-full"></div>
{t("vendor.analytics.pro_feature_4")}
                            </li>
                            <li className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-amber-300 rounded-full"></div>
{t("vendor.analytics.pro_feature_5")}
                            </li>
                          </ul>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <Button 
                            variant="hero"
                            size="lg" 
                            onClick={() => {
                              // TODO: Implement upgrade to PRO flow
                              toast({
title: t("vendor.toast.upgrade_pro_title"),
                                description: t("vendor.toast.upgrade_pro_desc"),
                                variant: "default"
                              });
                            }}
                          >
                            <Crown className="h-4 w-4 mr-2" />
{t("vendor.analytics.upgrade_btn")}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="lg"
                            className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10 hover:text-white"
                            onClick={() => {
                              // TODO: Implement contact support
                              toast({
title: t("vendor.toast.contact_support_title"),
                                description: t("vendor.toast.contact_support_desc"),
                                variant: "default"
                              });
                            }}
                          >
{t("vendor.analytics.contact_btn")}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="invoices" className="space-y-4">
                {company?.id && (
                  <CompanyInvoicesPanel
                    companyId={company.id}
                    bookings={bookings}
                    onRefresh={checkAuthAndFetchData}
                  />
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
      <BookingOpsDrawer 
        open={opsOpen}
        onClose={() => setOpsOpen(false)}
        booking={opsBooking}
        setBookingTimestamp={setBookingTimestamp}
        saveMetrics={saveMetrics}
        addChecklist={addChecklist}
        reportIncident={(id: string, d: any) => reportIncidentWith(id, d)}
        uploadPhoto={uploadPhoto}
        mEst={mEst} setMEst={setMEst}
        mAct={mAct} setMAct={setMAct}
        mTravel={mTravel} setMTravel={setMTravel}
        mMaterials={mMaterials} setMMaterials={setMMaterials}
        checkItem={checkItem} setCheckItem={setCheckItem}
        checkRequired={checkRequired} setCheckRequired={setCheckRequired}
        incidentCode={incidentCode} setIncidentCode={setIncidentCode}
        incidentNote={incidentNote} setIncidentNote={setIncidentNote}
        incidentTypes={incidentTypes}
      />
      {/* Reschedule Drawer */}
      <Drawer open={resDrawerOpen} onOpenChange={setResDrawerOpen}>
        <DrawerContent className="p-4">
          <DrawerHeader>
<DrawerTitle>{t("vendor.drawer.reschedule_title")}</DrawerTitle>
            <DrawerDescription>{t("vendor.drawer.reschedule_desc")}</DrawerDescription>
          </DrawerHeader>
          <div className="space-y-3 px-4">
            <div>
<Label>{t("vendor.drawer.new_date")}</Label>
              <div className="text-sm text-muted-foreground">{resDraft.newDate ? new Date(resDraft.newDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
<Label>{t("vendor.drawer.start_time")}</Label>
              <div className="text-sm text-muted-foreground">{resDraft.newTime || '-'}</div>
            </div>
            <div>
<Label>{t("vendor.drawer.reschedule_reason")}</Label>
              <Textarea value={resDraft.reason} onChange={(e) => setResDraft(prev => ({ ...prev, reason: e.target.value }))} placeholder={t("vendor.drawer.reason_required_ph")} />
            </div>
          </div>
          <DrawerFooter className="px-4">
<Button onClick={confirmReschedule}>{t("vendor.drawer.confirm")}</Button>
            <DrawerClose asChild>
<Button variant="outline">{t("vendor.drawer.cancel_btn")}</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      {/* Cancel Drawer */}
      <Drawer open={cancelDrawerOpen} onOpenChange={setCancelDrawerOpen}>
        <DrawerContent className="p-4">
          <DrawerHeader>
<DrawerTitle>{t("vendor.drawer.cancel_title")}</DrawerTitle>
            <DrawerDescription>{t("vendor.drawer.cancel_desc")}</DrawerDescription>
          </DrawerHeader>
          <div className="space-y-3 px-4">
            {cancelEstimate && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                <div className="flex items-center justify-between font-medium">
<span>{t("vendor.drawer.refund_estimate")}</span>
                  <span className={cancelEstimate.within48 ? 'text-amber-700' : 'text-emerald-700'}>
{cancelEstimate.within48 ? t('vendor.drawer.within_48h') : t('vendor.drawer.over_48h')}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
<div className="text-gray-600">{t("vendor.drawer.total")}</div>
                  <div className="text-right">{formatCurrency(cancelEstimate.total_amount || 0)}</div>
<div className="text-gray-600">{t("vendor.drawer.fee_retained")}</div>
                  <div className="text-right">{formatCurrency(((cancelEstimate.calculated_retained_cents || 0) / 100) || 0)}</div>
<div className="text-gray-600">{t("vendor.drawer.refund_estimated")}</div>
                  <div className="text-right font-semibold">{formatCurrency(((cancelEstimate.calculated_refund_cents || 0) / 100) || 0)}</div>
                </div>
                {typeof cancelEstimate.hours_until_service === 'number' && (
                  <div className="mt-2 text-xs text-gray-500">
{tx("vendor.drawer.hours_until", { hours: Math.round(cancelEstimate.hours_until_service) })}
                  </div>
                )}
              </div>
            )}
            <div>
<Label>{t("vendor.drawer.cancel_reason")}</Label>
              <Textarea value={cancelDraft.reason} onChange={(e) => setCancelDraft(prev => ({ ...prev, reason: e.target.value }))} placeholder={t("vendor.drawer.reason_required_ph")} />
            </div>
          </div>
          <DrawerFooter className="px-4">
            <Button variant="destructive" onClick={confirmCancel}>{t("vendor.drawer.confirm_cancel")}</Button>
            <DrawerClose asChild>
<Button variant="outline">{t("vendor.drawer.cancel_btn")}</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

// Inline drawer for booking ops
{/* Drawer markup */}
{/* We render here to keep within the same component file */}
{/* Simple right-side panel with backdrop */}
export function BookingOpsDrawer({ open, onClose, booking, setBookingTimestamp, saveMetrics, addChecklist, reportIncident, uploadPhoto, mEst, setMEst, mAct, setMAct, mTravel, setMTravel, mMaterials, setMMaterials, checkItem, setCheckItem, checkRequired, setCheckRequired, incidentCode, setIncidentCode, incidentNote, setIncidentNote, incidentTypes }: any) {
  const [photos, setPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [checklist, setChecklist] = useState<any[]>([]);
  const [photoModalOpen, setPhotoModalOpen] = useState<boolean>(false);
  const [photoType, setPhotoType] = useState<'pre'|'post'|'evidence'>('evidence');
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);
  const { toast } = useToast();
  const { t, tx } = useI18n();

  const handleDeletePhoto = async (photoId: string, photoUrl: string) => {
if (!window.confirm(t('vendor.ops.delete_photo_confirm'))) return;
    
    try {
      const isUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
      // Delete from storage first
      const { error: storageErr } = await (supabase as any)
        .storage.from('booking-photos')
        .remove([photoUrl]);
      
      if (storageErr) {
        console.warn('Storage deletion failed:', storageErr);
        // Continue with database deletion even if storage fails
      }

      // Delete from database
      let dbErr;
      if (isUuid(photoId)) {
        const resp = await (supabase as any)
          .from('booking_photos')
          .delete()
          .eq('id', photoId);
        dbErr = resp.error;
        // Fallback: also try by URL in case the preview row had a different id
        if (!dbErr) {
          await (supabase as any)
            .from('booking_photos')
            .delete()
            .eq('url', photoUrl);
        }
      } else {
        const resp = await (supabase as any)
          .from('booking_photos')
          .delete()
          .eq('url', photoUrl);
        dbErr = resp.error;
      }

      if (dbErr) throw dbErr;

      // Update local photos state immediately
      setPhotos(prev => prev.filter(p => p.id !== photoId && p.url !== photoUrl));
toast({ title: t('vendor.ops.photo_deleted') });
    } catch (e: any) {
      toast({ 
title: t('vendor.ops.delete_failed'), 
        description: e.message || t('vendor.ops.delete_failed_desc'), 
        variant: 'destructive' 
      });
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!open || !booking) return;
      setLoading(true);
      try {
        // fetch checklist items
        try {
          console.log('🔍 Fetching checklist for booking:', booking.id);
          const { data: chk, error: chkError } = await (supabase as any)
            .from('booking_checklists')
            .select('id, item, required, completed, completed_at, created_at')
            .eq('booking_id', booking.id)
            .order('created_at', { ascending: true });
          
          console.log('📋 Checklist query result:', { chk, chkError });
          if (chkError) {
            console.error('❌ Checklist query error:', chkError);
          }
          setChecklist(chk || []);
        } catch (error) {
          console.error('❌ Checklist fetch error:', error);
          setChecklist([]);
        }
        // fetch photo records and create signed URLs for display-time access
        const { data, error } = await (supabase as any)
          .from('booking_photos')
          .select('id, url, photo_type, created_at')
          .eq('booking_id', booking.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        let withSigned = await Promise.all((data || []).map(async (p: any) => {
          try {
            const { data: signed, error: signErr } = await (supabase as any)
              .storage.from('booking-photos')
              .createSignedUrl(p.url, 60 * 60); // 1 hour TTL
            if (signErr) throw signErr;
            return { ...p, signedUrl: signed?.signedUrl };
          } catch (_) {
            return { ...p, signedUrl: null };
          }
        }));
        // Fallback: if DB has no rows yet, try to list storage folder and create previews
        if ((withSigned || []).length === 0) {
          try {
            const { data: list } = await (supabase as any)
              .storage.from('booking-photos')
              .list(`${booking.id}`, { limit: 20, offset: 0, sortBy: { column: 'created_at', order: 'desc' } });
            const fromStorage = await Promise.all((list || []).map(async (f: any) => {
              const path = `${booking.id}/${f.name}`;
              try {
                const { data: signed } = await (supabase as any).storage.from('booking-photos').createSignedUrl(path, 60 * 60);
                return { id: path, url: path, photo_type: f.metadata?.type || 'evidence', created_at: f.created_at, signedUrl: signed?.signedUrl };
              } catch { return { id: path, url: path, photo_type: 'evidence', created_at: f.created_at, signedUrl: null }; }
            }));
            withSigned = fromStorage;
          } catch {}
        }
        setPhotos(withSigned);
      } catch (_) {
        setPhotos([]);
        setChecklist([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, booking]);

  if (!open || !booking) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 app-dialog-backdrop" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] app-drawer-panel p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-gray-500">{t("vendor.ops.booking")}</div>
            <div className="font-semibold">{booking.booking_number}</div>
          </div>
          <Button variant="outline" onClick={onClose}>{t("vendor.ops.close")}</Button>
        </div>
        <div className="space-y-6">
          <div>
<div className="font-medium mb-2">{t("vendor.ops.timestamps")}</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setBookingTimestamp(booking.id, 'assigned_at')}>{t('vendor.ops.assigned')}</Button>
              <Button size="sm" variant="outline" onClick={() => setBookingTimestamp(booking.id, 'depart_at')}>{t('vendor.ops.depart')}</Button>
              <Button size="sm" variant="outline" onClick={() => setBookingTimestamp(booking.id, 'arrive_at')}>{t('vendor.ops.arrive')}</Button>
              <Button size="sm" variant="outline" onClick={() => setBookingTimestamp(booking.id, 'start_at')}>{t('vendor.ops.start_at')}</Button>
              <Button size="sm" variant="outline" onClick={() => setBookingTimestamp(booking.id, 'finish_at')}>{t('vendor.ops.finish')}</Button>
            </div>
          </div>

          <div>
<div className="font-medium mb-2">{t("vendor.ops.metrics")}</div>
            <div className="grid grid-cols-2 gap-3">
              <input 
                className="border rounded px-2 py-1" 
                placeholder={t("vendor.ops.labor_est")} 
                type="number" 
                min="0" 
                step="1"
                value={mEst} 
                onChange={e => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setMEst(value);
                  }
                }} 
              />
              <input 
                className="border rounded px-2 py-1" 
                placeholder={t("vendor.ops.labor_actual")} 
                type="number" 
                min="0" 
                step="1"
                value={mAct} 
                onChange={e => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setMAct(value);
                  }
                }} 
              />
              <input 
                className="border rounded px-2 py-1" 
                placeholder={t("vendor.ops.travel")} 
                type="number" 
                min="0" 
                step="1"
                value={mTravel} 
                onChange={e => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setMTravel(value);
                  }
                }} 
              />
              <input 
                className="border rounded px-2 py-1" 
                placeholder={t("vendor.ops.materials")} 
                type="number" 
                min="0" 
                step="0.01"
                value={mMaterials} 
                onChange={e => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setMMaterials(value);
                  }
                }} 
              />
            </div>
            <div className="mt-2">
              <Button size="sm" onClick={() => saveMetrics(booking.id, { mEst, mAct, mTravel, mMaterials })}>{t('vendor.ops.save')}</Button>
            </div>
          </div>

          <div>
            <div className="font-medium mb-2 flex items-center justify-between">
<span>{t("vendor.ops.checklist")}</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) return;
                    const r = await fetch(`/api/ops?action=list_checklist&booking_id=${encodeURIComponent(booking.id)}`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
                    const d = await r.json();
                    console.log('Manual refresh - checklist items:', d?.items);
                    setChecklist(d?.items || []);
                  } catch (e) {
                    console.error('Failed to refresh checklist:', e);
                  }
                }}
              >
                {t('vendor.ops.refresh')}
              </Button>
            </div>
            <div className="flex gap-2">
              <input className="border rounded px-2 py-1 flex-1" placeholder={t("vendor.ops.item")} value={checkItem} onChange={e => setCheckItem(e.target.value)} />
              <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={checkRequired} onChange={e => setCheckRequired(e.target.checked)} /> {t('vendor.ops.required')}</label>
            </div>
            <div className="mt-2">
              <Button size="sm" onClick={async () => { 
                if (!checkItem.trim()) return;
                try {
                  await addChecklist(booking.id, { item: checkItem, required: checkRequired }); 
                  setCheckItem(''); 
                  setCheckRequired(false);
                  
                  // Force reload checklist items with a small delay to ensure DB is updated
                  setTimeout(async () => {
                    try { 
                      const { data: { session } } = await supabase.auth.getSession();
                      if (!session) return;
                      const r = await fetch(`/api/ops?action=list_checklist&booking_id=${encodeURIComponent(booking.id)}`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
                      const d = await r.json();
                      console.log('Reloaded checklist items:', d?.items);
                      setChecklist(d?.items || []); 
                    } catch (e) {
                      console.error('Failed to reload checklist:', e);
                    }
                  }, 500);
                } catch (error) {
                  console.error('Error adding checklist item:', error);
                }
              }} disabled={!checkItem.trim()}>
{t('vendor.ops.add_item')}
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {/* Debug: rendering checklist items */}
              {checklist.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={!!c.completed} onChange={async (e) => {
                      const val = e.target.checked;
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (!session) return;
                        const r = await fetch('/api/ops?action=update_checklist', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                          body: JSON.stringify({ id: c.id, booking_id: booking.id, completed: val })
                        });
                        if (!r.ok) throw new Error((await r.json()).error || 'Update failed');
                        setChecklist(prev => prev.map(x => x.id === c.id ? { ...x, completed: val, completed_at: val ? new Date().toISOString() : null } : x));
                      } catch {}
                    }} />
                    <span className="text-sm">{c.item}</span>
                    {c.required && <span className="text-xs text-red-600">{t('vendor.ops.required_tag')}</span>}
                  </div>
                  {c.completed_at && <span className="text-xs text-gray-500">{new Date(c.completed_at).toLocaleString()}</span>}
                </div>
              ))}
              {checklist.length === 0 && <div className="text-xs text-gray-500">{t('vendor.ops.no_items')}</div>}
            </div>
          </div>

          <div>
<div className="font-medium mb-2">{t("vendor.ops.incident")}</div>
            <Select value={incidentCode} onValueChange={setIncidentCode}>
              <SelectTrigger className="w-full mb-2">
                <SelectValue placeholder={t("vendor.ops.incident_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {incidentTypes.map((type) => (
                  <SelectItem key={type.id} value={type.code}>
                    {type.label} ({type.category})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <textarea className="border rounded px-2 py-1 w-full" rows={3} placeholder={t("vendor.ops.incident_details")} value={incidentNote} onChange={e => setIncidentNote(e.target.value)} />
            <div className="mt-2">
              <Button size="sm" onClick={() => reportIncident(booking.id, { code: incidentCode, note: incidentNote })} disabled={!incidentCode}>
                {t('vendor.ops.report_incident')}
              </Button>
            </div>
          </div>

          <div>
<div className="font-medium mb-2">{t("vendor.ops.photo")}</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPhotoModalOpen(true)}>{t('vendor.ops.upload_photo')}</Button>
              {loading && <span className="text-xs text-gray-500">{t('vendor.ops.loading')}</span>}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {photos.map((p, idx) => (
                <div key={p.id} className="relative border rounded overflow-hidden">
                  <button 
                    className="w-full text-left" 
                    onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                  >
                    {p.signedUrl ? (
                      <img src={p.signedUrl} alt={p.photo_type} className="w-full h-24 object-cover" />
                    ) : (
                      <div className="w-full h-24 bg-gray-100 flex items-center justify-center text-xs text-gray-500">{t('vendor.ops.no_preview')}</div>
                    )}
                    <div className="p-1 text-[10px] text-gray-600 truncate">{p.photo_type}</div>
                  </button>
                  <button
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center hover:bg-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(p.id, p.url);
                    }}
                    title={t("vendor.ops.delete_photo_title")}
                  >
                    ×
                  </button>
                </div>
              ))}
              {photos.length === 0 && !loading && (
                <div className="text-xs text-gray-500">{t('vendor.ops.no_photos')}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Photo upload modal */}
      {photoModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !uploading && setPhotoModalOpen(false)} />
          <div className="app-modal-panel relative w-[90%] max-w-md p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">{t("vendor.ops.upload_photo")}</div>
              <Button variant="outline" size="sm" onClick={() => !uploading && setPhotoModalOpen(false)}>{t('vendor.ops.close')}</Button>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium mb-1">{t("vendor.ops.photo_type")}</div>
                <div className="flex items-center gap-3 text-sm">
                  <label className="flex items-center gap-1"><input type="radio" name="ptype" checked={photoType==='pre'} onChange={() => setPhotoType('pre')} /> pre</label>
                  <label className="flex items-center gap-1"><input type="radio" name="ptype" checked={photoType==='post'} onChange={() => setPhotoType('post')} /> post</label>
                  <label className="flex items-center gap-1"><input type="radio" name="ptype" checked={photoType==='evidence'} onChange={() => setPhotoType('evidence')} /> evidence</label>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">{t("vendor.ops.select_files")}</div>
                <input multiple type="file" accept="image/*" onChange={(e) => {
                  const list = e.target.files ? Array.from(e.target.files) : [];
                  setPhotoFiles((prev: File[]) => [...prev, ...list]);
                  try { (e.target as any).value = ''; } catch {}
                }} />
                {photoFiles.length > 0 && (
                  <div className="mt-2 text-xs text-gray-600">{tx("vendor.ops.files_selected", { count: photoFiles.length })}</div>
                )}
              </div>
              <div className="pt-2 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => !uploading && setPhotoModalOpen(false)} disabled={uploading}>{t('vendor.drawer.cancel_btn')}</Button>
                <Button size="sm" onClick={async () => {
                  if (!photoFiles.length) return;
                  try {
                    setUploading(true);
                    for (const f of photoFiles) {
                      const key = `${booking.id}/${Date.now()}-${f.name}`;
                      const { error: upErr } = await (supabase as any).storage.from('booking-photos').upload(key, f, { upsert: false });
                      if (upErr) throw upErr;
                      const { error: insErr } = await (supabase as any).from('booking_photos').insert({ booking_id: booking.id, url: key, photo_type: photoType });
                      if (insErr) throw insErr;
                    }
                    // refresh gallery
                    try {
                      const { data } = await (supabase as any)
                        .from('booking_photos')
                        .select('id, url, photo_type, created_at')
                        .eq('booking_id', booking.id)
                        .order('created_at', { ascending: false });
                      const withSigned = await Promise.all((data || []).map(async (p: any) => {
                        try {
                          const { data: signed } = await (supabase as any).storage.from('booking-photos').createSignedUrl(p.url, 60 * 60);
                          return { ...p, signedUrl: signed?.signedUrl };
                        } catch { return { ...p, signedUrl: null }; }
                      }));
                      setPhotos(withSigned);
                    } catch {}
                    setPhotoFiles([]);
                    setPhotoType('evidence');
                    setPhotoModalOpen(false);
                    (window as any).toast?.({ title: t('vendor.toast.photos_uploaded') });
                  } catch (e: any) {
                    (window as any).toast?.({ title: t('vendor.toast.upload_failed'), description: e?.message || t('vendor.toast.error'), variant: 'destructive' });
                  } finally {
                    setUploading(false);
                  }
                }} disabled={!photoFiles.length || uploading}>{uploading ? t('vendor.ops.uploading') : t('vendor.ops.upload')}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Lightbox */}
      {lightboxOpen && photos[lightboxIndex] && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setLightboxOpen(false)} />
          <div className="relative max-w-3xl w-[90%] bg-black rounded-xl p-3 flex flex-col items-center">
            <img src={photos[lightboxIndex].signedUrl || ''} alt={photos[lightboxIndex].photo_type} className="max-h-[80vh] object-contain" />
            <div className="absolute top-3 right-3 flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setLightboxOpen(false)}>{t('vendor.ops.close')}</Button>
            </div>
            <div className="absolute inset-y-0 left-2 flex items-center">
              <Button size="sm" variant="outline" onClick={() => setLightboxIndex((i) => (i - 1 + photos.length) % photos.length)}>{'<'}</Button>
            </div>
            <div className="absolute inset-y-0 right-2 flex items-center">
              <Button size="sm" variant="outline" onClick={() => setLightboxIndex((i) => (i + 1) % photos.length)}>{'>'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}